/**
 * Utility functions for passenger price calculations
 */

/**
 * Calculate the total amount for a passenger based on excursion prices and quantities
 *
 * @param {Object} params
 * @param {number} params.adultsCount - Number of adults
 * @param {number} params.minorsCount - Number of minors
 * @param {number} params.adultsAbsentCount - Number of absent adults (optional)
 * @param {number} params.minorsAbsentCount - Number of absent minors (optional)
 * @param {number} params.priceAdult - Base price per adult
 * @param {number} params.priceMinor - Base price per minor
 * @param {number} params.customPriceAdult - Custom price per adult (overrides base price)
 * @param {number} params.customPriceMinor - Custom price per minor (overrides base price)
 * @param {number} params.discountPercentage - Discount percentage (0-100)
 * @returns {number} The calculated total amount
 */
export function calculatePassengerTotal({
  adultsCount = 0,
  minorsCount = 0,
  adultsAbsentCount = 0,
  minorsAbsentCount = 0,
  priceAdult = 0,
  priceMinor = 0,
  customPriceAdult = null,
  customPriceMinor = null,
  discountPercentage = null,
}) {
  // Calculate effective prices
  let effectivePriceAdult = priceAdult;
  let effectivePriceMinor = priceMinor;

  if (customPriceAdult !== null && customPriceAdult !== undefined) {
    effectivePriceAdult = customPriceAdult;
  } else if (discountPercentage !== null && discountPercentage !== undefined) {
    effectivePriceAdult = priceAdult * (1 - discountPercentage / 100);
  }

  if (customPriceMinor !== null && customPriceMinor !== undefined) {
    effectivePriceMinor = customPriceMinor;
  } else if (discountPercentage !== null && discountPercentage !== undefined) {
    effectivePriceMinor = priceMinor * (1 - discountPercentage / 100);
  }

  const adultPayment = adultsCount * effectivePriceAdult;
  const minorPayment = minorsCount * effectivePriceMinor;
  const absenceAdultPayment = adultsAbsentCount * effectivePriceAdult;
  const absenceMinorPayment = minorsAbsentCount * effectivePriceMinor;

  return adultPayment + minorPayment - absenceAdultPayment - absenceMinorPayment;
}

/**
 * Calculate the total amount using excursion object directly
 *
 * @param {Object} params
 * @param {number} params.adultsCount - Number of adults
 * @param {number} params.minorsCount - Number of minors
 * @param {number} params.adultsAbsentCount - Number of absent adults (optional)
 * @param {number} params.minorsAbsentCount - Number of absent minors (optional)
 * @param {Object} params.excursion - Excursion object with excursionType.priceAdult and priceMinor
 * @returns {number} The calculated total amount
 */
export function calculatePassengerTotalFromExcursion({
  adultsCount = 0,
  minorsCount = 0,
  adultsAbsentCount = 0,
  minorsAbsentCount = 0,
  excursion,
}) {
  if (!excursion?.excursionType) {
    return 0;
  }

  return calculatePassengerTotal({
    adultsCount,
    minorsCount,
    adultsAbsentCount,
    minorsAbsentCount,
    priceAdult: excursion.excursionType.priceAdult || 0,
    priceMinor: excursion.excursionType.priceMinor || 0,
  });
}

/**
 * Calculate debt (remaining amount to pay)
 *
 * @param {number} totalAmount - Total amount due
 * @param {number} amountPaid - Amount already paid
 * @returns {number} The remaining debt (0 if overpaid)
 */
export function calculateDebt(totalAmount, amountPaid) {
  const debt = totalAmount - amountPaid;
  return debt > 0 ? debt : 0;
}
